import React, { useState } from "react";
import {
  Box,
  CircularProgress,
  Divider,
  MenuItem,
  Typography,
} from "@mui/material";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { Account } from "@bn/api/apiv1";
import { MultibankingApiProvider, useMyself } from "@api";
import UpdateIcon from "@icons/myicons/refresh.svg?react";
import UploadIcon from "@icons/myicons/cloud-storage-upload.svg?react";
import AddIcon from "@icons/myicons/plus.svg?react";
import EditIcon from "@icons/myicons/edit.svg?react";
import UpdateHistoryIcon from "@icons/myicons/time-clock-menu.svg?react";
import ManageBankConnectionIcon from "@icons/myicons/bank-edit.svg?react";
import HideIcon from "@icons/myicons/eye-cross.svg?react";
import UnhideIcon from "@icons/myicons/eye.svg?react";
import CancelIcon from "@icons/myicons/delete-circle.svg?react";
import TransferIcon from "@icons/myicons/wallet.svg?react";
import { useRoutes } from "@hooks";

interface Props {
  account?: Account;
  handleOpenDeleteModal: () => void;
  handleHideAccount: () => void;
  ref?: React.Ref<HTMLDivElement>;
}

export const DepotMenu = (props: Props) => {
  const { handleOpenDeleteModal, handleHideAccount, account, ref } = props;
  const { t } = useTranslation();
  const { data: myself } = useMyself();
  const isQA = myself?.roles?.includes("ROLE_QUALITY_ASSURANCE");
  const { pushUpdateConnection } = useRoutes();

  const [isUpdating, setIsUpdating] = useState(false);

  const handleReconnect = async () => {};
  const updateAccount = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!shouldDisplayUpdateButton || isUpdating) return;
    setIsUpdating(true);
    try {
      await pushUpdateConnection(
        account?.bankConnectionId!,
        account?.bankConnectionType as MultibankingApiProvider
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const shouldDisplayUpdateButton =
    Boolean(account?.bankConnectionType) &&
    account?.bankConnectionType !== "NETFONDS";

  return (
    <div ref={ref}>
      {shouldDisplayUpdateButton && (
        <MenuItem
          data-testid="depot-update"
          onClick={updateAccount}
          disabled={isUpdating}
        >
          <Box display="flex" alignItems="center">
            {isUpdating ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <UpdateIcon />
            )}
            <Typography style={{ marginLeft: "12px" }} variant="inherit">
              {t("depotPage.headerMenu.update")}
            </Typography>
          </Box>
        </MenuItem>
      )}
      {/*@ts-ignore*/}
      {isQA && account?.bankConnectionStatus?.status === "INACTIVE" && (
        <MenuItem data-testid="depot-reconnect" onClick={handleReconnect}>
          <Box display="flex" alignItems="center">
            <UpdateIcon />
            <Typography style={{ marginLeft: "12px" }} variant="inherit">
              {t("depotPage.headerMenu.reconnect")}
            </Typography>
          </Box>
        </MenuItem>
      )}
      <MenuItem
        data-testid="pdf-upload"
        component={Link}
        to={`/depots/${account?.id}/file-drop`}
      >
        <Box display="flex" alignItems="center">
          <UploadIcon />
          <Typography style={{ marginLeft: "12px" }} variant="inherit">
            {t("depotPage.headerMenu.upload")}
          </Typography>
        </Box>
      </MenuItem>
      <MenuItem
        data-testid="csv-upload"
        component={Link}
        to={`/depots/${account?.id}/csv-drop`}
      >
        <Box display="flex" alignItems="center">
          <UploadIcon />
          <Typography style={{ marginLeft: "12px" }} variant="inherit">
            {t("depotPage.headerMenu.uploadCsv")}
          </Typography>
        </Box>
      </MenuItem>
      <MenuItem
        data-testid="add-investment"
        component={Link}
        to={`/new-transaction-wizard?depotId=${account?.id}`}
      >
        <Box display="flex" alignItems="center">
          <AddIcon width="24px" height="14px" />
          <Typography style={{ marginLeft: "12px" }} variant="inherit">
            {t("depotPage.headerMenu.addButton")}
          </Typography>
        </Box>
      </MenuItem>
      <MenuItem
        data-testid="edit"
        component={Link}
        to={`/depots/${account?.id}/edit`}
      >
        <Box display="flex" alignItems="center">
          <EditIcon />
          <Typography style={{ marginLeft: "12px" }} variant="inherit">
            {t("depotPage.headerMenu.edit")}
          </Typography>
        </Box>
      </MenuItem>
      <MenuItem
        data-testid="synchronization-log"
        component={Link}
        to={`/synchronization-logs/${account?.id}`}
      >
        <Box display="flex" alignItems="center">
          <UpdateHistoryIcon />
          <Typography style={{ marginLeft: "12px" }} variant="inherit">
            {t("depotPage.headerMenu.showUpdateHistory")}
          </Typography>
        </Box>
      </MenuItem>
      {Boolean(account?.bankConnectionType) && (
        <MenuItem
          data-testid="manage-bank-connection"
          component={Link}
          to={`/settings/bankConnections/${account?.bankConnectionType}/${account?.bankConnectionId}`}
        >
          <Box display="flex" alignItems="center">
            <ManageBankConnectionIcon />
            <Typography style={{ marginLeft: "12px" }} variant="inherit">
              {t("depotPage.headerMenu.manageBankConnection")}
            </Typography>
          </Box>
        </MenuItem>
      )}
      <Divider />
      <MenuItem
        data-testid="depot-transfer"
        component={Link}
        to={`/depots/${account?.id}/transfer?redirectUrl=${
          import.meta.env.VITE_APP_APP_URL
        }/depots/${account?.id}`}
      >
        <Box display="flex" alignItems="center">
          <TransferIcon />
          <Typography style={{ marginLeft: "12px" }} variant="inherit">
            {t("depotPage.headerMenu.transfer")}
          </Typography>
        </Box>
      </MenuItem>
      {Boolean(account?.bankConnectionType) && (
        <MenuItem
          data-testid="change-account"
          component={Link}
          to={`/depots/${account?.id}/assign-contra-account`}
        >
          <Box display="flex" alignItems="center">
            <EditIcon />
            <Typography style={{ marginLeft: "12px" }} variant="inherit">
              {t("depotPage.headerMenu.changeAccount")}
            </Typography>
          </Box>
        </MenuItem>
      )}
      <MenuItem
        onClick={handleHideAccount}
        data-testid={account?.isHidden ? "unhide-depot" : "hide-depot"}
      >
        <Box display="flex" alignItems="center">
          {account?.isHidden ? <UnhideIcon /> : <HideIcon />}
          <Typography style={{ marginLeft: "12px" }} variant="inherit">
            {account?.isHidden
              ? t("depotPage.headerMenu.show")
              : t("depotPage.headerMenu.hide")}
          </Typography>
        </Box>
      </MenuItem>
      <Divider />
      <MenuItem data-testid="delete">
        <Box
          display="flex"
          alignItems="center"
          onClick={handleOpenDeleteModal}
          color="error.main"
        >
          <CancelIcon />
          <Typography style={{ marginLeft: "12px" }} variant="inherit">
            {t("depotPage.headerMenu.delete")}
          </Typography>
        </Box>
      </MenuItem>
    </div>
  );
};
